/**
 * m3 echo Frontend - Support Inbox Dialog Component
 *
 * Admin view to manage support tickets.
 * Shows list of tickets with ability to view details, mark as resolved, or delete.
 *
 * Security:
 * - All user-submitted content is escaped to prevent XSS
 * - Attachment downloads are validated
 */

import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CircleIcon from '@mui/icons-material/Circle';
import { supportApi } from '../services/api';
import { SupportTicket } from '../types/api';

interface SupportInboxDialogProps {
  open: boolean;
  onClose: () => void;
}

// Allowed file extensions for attachment downloads
const ALLOWED_ATTACHMENT_EXTENSIONS = new Set([
  '.png', '.jpg', '.jpeg', '.gif', '.pdf', '.txt', '.doc', '.docx', '.csv', '.xlsx'
]);

/**
 * Sanitize text content to prevent XSS attacks.
 * Uses DOMPurify to strip all HTML tags while preserving text.
 */
const sanitizeText = (text: string | null | undefined): string => {
  if (!text) return '';
  // Strip all HTML tags - we only want plain text
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

/**
 * Validate attachment filename for security.
 */
const isValidAttachmentFilename = (filename: string | null | undefined): boolean => {
  if (!filename) return false;

  // Check for directory traversal attempts
  if (filename.includes('..') || filename.includes('/') || filename.includes('\\')) {
    return false;
  }

  // Check extension
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return false;

  const ext = filename.substring(lastDot).toLowerCase();
  return ALLOWED_ATTACHMENT_EXTENSIONS.has(ext);
};

const SupportInboxDialog: React.FC<SupportInboxDialogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [tabValue, setTabValue] = useState(0); // 0 = open, 1 = resolved
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      fetchTickets();
    }
  }, [open, tabValue]);

  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const statusFilter = tabValue === 0 ? 'open' : 'resolved';
      const response = await supportApi.getAll({ status_filter: statusFilter, limit: 100 });
      setTickets(response.data.tickets);
    } catch (err: any) {
      console.error('Error fetching tickets:', err);
      setError(err.message || 'Fehler beim Laden der Tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = async (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    // Mark as read if not already
    if (!ticket.is_read) {
      try {
        await supportApi.update(ticket.id, { is_read: true });
        // Update local state
        setTickets((prev) =>
          prev.map((t) => (t.id === ticket.id ? { ...t, is_read: true } : t))
        );
      } catch (err) {
        console.error('Error marking ticket as read:', err);
      }
    }
  };

  const handleMarkResolved = async (ticketId: number) => {
    setActionLoading(ticketId);
    try {
      await supportApi.update(ticketId, { status: 'resolved' });
      // Refresh list
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(null);
      }
    } catch (err: any) {
      console.error('Error marking ticket as resolved:', err);
      setError(err.message || 'Fehler beim Aktualisieren');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReopen = async (ticketId: number) => {
    setActionLoading(ticketId);
    try {
      await supportApi.update(ticketId, { status: 'open' });
      // Refresh list
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(null);
      }
    } catch (err: any) {
      console.error('Error reopening ticket:', err);
      setError(err.message || 'Fehler beim Aktualisieren');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (ticketId: number) => {
    if (!window.confirm('Ticket wirklich loeschen?')) return;

    setActionLoading(ticketId);
    try {
      await supportApi.delete(ticketId);
      // Refresh list
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(null);
      }
    } catch (err: any) {
      console.error('Error deleting ticket:', err);
      setError(err.message || 'Fehler beim Loeschen');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadAttachment = (ticket: SupportTicket) => {
    if (!ticket.attachment_data || !ticket.attachment_filename) return;

    // Validate filename before download
    if (!isValidAttachmentFilename(ticket.attachment_filename)) {
      setError('Ungültiger Dateiname - Download nicht möglich');
      console.error('Invalid attachment filename:', ticket.attachment_filename);
      return;
    }

    try {
      // Create blob from base64
      const byteCharacters = atob(ticket.attachment_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray]);

      // Create download link with sanitized filename
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Sanitize filename - remove any remaining unsafe characters
      const safeFilename = ticket.attachment_filename.replace(/[<>:"|?*]/g, '_');
      a.download = safeFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      setError('Fehler beim Herunterladen des Anhangs');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleClose = () => {
    setSelectedTicket(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle>Support-Anfragen</DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="Offen" />
            <Tab label="Geloest" />
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : selectedTicket ? (
          // Detail view
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Button size="small" onClick={() => setSelectedTicket(null)}>
                Zurueck zur Liste
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {selectedTicket.status === 'open' ? (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => handleMarkResolved(selectedTicket.id)}
                    disabled={actionLoading === selectedTicket.id}
                  >
                    Als geloest markieren
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UndoIcon />}
                    onClick={() => handleReopen(selectedTicket.id)}
                    disabled={actionLoading === selectedTicket.id}
                  >
                    Wieder oeffnen
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDelete(selectedTicket.id)}
                  disabled={actionLoading === selectedTicket.id}
                >
                  Loeschen
                </Button>
              </Box>
            </Box>

            <Typography variant="h6" gutterBottom>
              {/* Sanitize title to prevent XSS */}
              {sanitizeText(selectedTicket.title)}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="textSecondary">
                {/* Sanitize sender info */}
                Von: {sanitizeText(selectedTicket.sender?.email) || 'Unbekannt'}
                {selectedTicket.sender?.full_name && ` (${sanitizeText(selectedTicket.sender.full_name)})`}
              </Typography>
              {selectedTicket.sender?.tenant_name && (
                <Typography variant="body2" color="textSecondary">
                  Mandant: {sanitizeText(selectedTicket.sender.tenant_name)}
                </Typography>
              )}
              <Typography variant="body2" color="textSecondary">
                {formatDate(selectedTicket.created_at)}
              </Typography>
              <Chip
                label={selectedTicket.status === 'open' ? 'Offen' : 'Geloest'}
                size="small"
                color={selectedTicket.status === 'open' ? 'warning' : 'success'}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="body1"
              sx={{ whiteSpace: 'pre-wrap', mb: 2 }}
            >
              {/* Sanitize message content to prevent XSS */}
              {sanitizeText(selectedTicket.message)}
            </Typography>

            {selectedTicket.attachment_filename && isValidAttachmentFilename(selectedTicket.attachment_filename) && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AttachFileIcon />}
                  onClick={() => handleDownloadAttachment(selectedTicket)}
                >
                  {/* Sanitize attachment filename */}
                  Anhang herunterladen: {sanitizeText(selectedTicket.attachment_filename)}
                </Button>
              </Box>
            )}

            {selectedTicket.resolved_at && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                Geloest am: {formatDate(selectedTicket.resolved_at)}
              </Typography>
            )}
          </Paper>
        ) : tickets.length === 0 ? (
          <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
            Keine {tabValue === 0 ? 'offenen' : 'geloesten'} Tickets vorhanden.
          </Typography>
        ) : (
          // List view
          <List>
            {tickets.map((ticket, index) => (
              <React.Fragment key={ticket.id}>
                <ListItem
                  button
                  onClick={() => handleViewTicket(ticket)}
                  sx={{
                    bgcolor: !ticket.is_read ? 'action.hover' : 'transparent',
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    {!ticket.is_read && (
                      <Tooltip title="Ungelesen">
                        <CircleIcon sx={{ fontSize: 10, color: 'primary.main' }} />
                      </Tooltip>
                    )}
                  </Box>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: !ticket.is_read ? 600 : 400 }}
                        >
                          {/* Sanitize title in list view */}
                          {sanitizeText(ticket.title)}
                        </Typography>
                        {ticket.attachment_filename && isValidAttachmentFilename(ticket.attachment_filename) && (
                          <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="textSecondary" component="span">
                          {/* Sanitize sender info in list view */}
                          {sanitizeText(ticket.sender?.email) || 'Unbekannt'}
                          {ticket.sender?.tenant_name && ` - ${sanitizeText(ticket.sender.tenant_name)}`}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" component="span" sx={{ ml: 2 }}>
                          {formatDate(ticket.created_at)}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    {ticket.status === 'open' ? (
                      <Tooltip title="Als geloest markieren">
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkResolved(ticket.id);
                          }}
                          disabled={actionLoading === ticket.id}
                          color="success"
                        >
                          {actionLoading === ticket.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CheckCircleIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Wieder oeffnen">
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReopen(ticket.id);
                          }}
                          disabled={actionLoading === ticket.id}
                        >
                          {actionLoading === ticket.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <UndoIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Loeschen">
                      <IconButton
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(ticket.id);
                        }}
                        disabled={actionLoading === ticket.id}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < tickets.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Schliessen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupportInboxDialog;
